import React from 'react';
import { ProgressBar, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome, 
  faClipboardCheck, 
  faChartLine, 
  faTachometerAlt 
} from '@fortawesome/free-solid-svg-icons';
import { useWorkflow } from './WorkflowProvider';

interface StepInfo {
  id: number;
  title: string;
  icon: any;
  description: string;
}

const steps: StepInfo[] = [
  {
    id: 1,
    title: 'Property Discovery',
    icon: faHome,
    description: 'Find and input property details'
  },
  {
    id: 2,
    title: 'Comparable Analysis',
    icon: faClipboardCheck,
    description: 'Find comparables and run XGBoost model'
  },
  {
    id: 3,
    title: 'Price Prediction',
    icon: faChartLine,
    description: 'Review ML prediction results'
  },
  {
    id: 4,
    title: 'Strategic Insights',
    icon: faTachometerAlt,
    description: 'AI-powered market strategy & pricing'
  }
];

const StepIndicator: React.FC = () => {
  const { state } = useWorkflow();
  const { currentStep, progress, currentOperation } = state;

  return (
    <Card className="mb-4">
      <Card.Body className="pb-2">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">
            🏠 Smart CMA Analysis - Step {currentStep} of 4
          </h5>
          <span className="text-muted">
            {Math.round(progress)}% Complete
          </span>
        </div>

        {/* Progress Bar */}
        <ProgressBar 
          now={progress} 
          className="mb-3"
          style={{ height: '8px' }}
          variant="success"
        />

        {/* Step Indicators */}
        <div className="d-flex justify-content-between align-items-start">
          {steps.map((step, index) => {
            const isActive = step.id === currentStep;
            const isCompleted = step.id < currentStep;
            const isAccessible = step.id <= currentStep;

            return (
              <div 
                key={step.id}
                className={`text-center flex-grow-1 ${index < steps.length - 1 ? 'me-3' : ''}`}
                style={{ maxWidth: '200px' }}
              >
                {/* Step Circle */}
                <div
                  className={`rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2 ${
                    isCompleted 
                      ? 'bg-success text-white' 
                      : isActive 
                      ? 'bg-primary text-white' 
                      : 'bg-light text-muted'
                  }`}
                  style={{ 
                    width: '48px', 
                    height: '48px',
                    border: isAccessible ? '2px solid' : '2px solid #e9ecef',
                    borderColor: isCompleted ? '#198754' : isActive ? '#0d6efd' : '#e9ecef'
                  }}
                >
                  <FontAwesomeIcon 
                    icon={step.icon} 
                    size="lg"
                  />
                </div>

                {/* Step Title */}
                <h6 
                  className={`mb-1 ${
                    isAccessible ? 'text-dark' : 'text-muted'
                  }`}
                  style={{ fontSize: '0.9rem' }}
                >
                  {step.title}
                </h6>

                {/* Step Description */}
                <small 
                  className={`d-block ${
                    isActive ? 'text-primary' : 'text-muted'
                  }`}
                  style={{ fontSize: '0.75rem' }}
                >
                  {step.description}
                </small>

                {/* Connection Line */}
                {index < steps.length - 1 && (
                  <div
                    className={`position-absolute ${
                      isCompleted ? 'bg-success' : 'bg-light'
                    }`}
                    style={{
                      top: '24px',
                      left: '50%',
                      right: '-50%',
                      height: '2px',
                      zIndex: -1,
                      transform: 'translateY(-50%)'
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Current Operation */}
        {currentOperation && (
          <div className="mt-3 p-2 bg-light rounded">
            <div className="d-flex align-items-center">
              <div 
                className="spinner-border spinner-border-sm text-primary me-2" 
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <small className="text-muted">
                {currentOperation}
              </small>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default StepIndicator; 